import React from "react";
import { Icon, EmptyState, confirmDialog } from "./registry";
import { downscaleToDataUrl } from "./lib/img";
/* ============================================================
   Fence Works — Job Packet (its own page)
   Left: what prints when a quote's "Job packet" opens — the
   generated sheets (blueprint, install specs & pre-job) plus the
   contractor's own uploaded pages, ordered and toggleable.
   Right: the SWAG PACK builder — pick print & swag products,
   set quantities, and send the order to the partner (Vistaprint
   affiliate link-out; Kodiak Wholesale order email prefilled with
   the pack + company info). Every click/order logs an event for
   affiliate-revenue attribution.
   ============================================================ */

/* Partner links — replace with the LIVE affiliate-tagged URLs.
   Kodiak Wholesale is kodiak-wholesale.com (Robby's shop). */
const SWAG_PARTNERS: any = {
  vistaprint: {
    id: "vistaprint", name: "Vistaprint", tag: "PRINT PARTNER",
    blurb: "Folders, cards & yard signs — get the packet printed and branded.",
    // TODO(affiliate): swap for the live Vistaprint affiliate link (CJ/Impact)
    url: "https://www.vistaprint.com/?utm_source=fenceworks&utm_medium=app&utm_campaign=swag-pack",
  },
  kodiak: {
    id: "kodiak", name: "Kodiak Wholesale", tag: "SWAG PARTNER",
    blurb: "Engraved drinkware, custom hats & crew apparel — gifts customers keep.",
    url: "https://www.kodiak-wholesale.com/?utm_source=fenceworks&utm_medium=app&utm_campaign=swag-pack",
    // TODO(affiliate): confirm the order-desk address with Kodiak Wholesale
    orderEmail: "orders@kodiak-wholesale.com",
  },
};

/* the pickable catalog — partner-grouped; contractors choose what goes in
   THEIR pack (quantities per item), then the order goes out per partner */
const SWAG_ITEMS = [
  { partner: "vistaprint", id: "folders", name: "Presentation folders", note: "9×12 · packet covers, full color" },
  { partner: "vistaprint", id: "cards", name: "Business cards", note: "leave-behinds for every visit" },
  { partner: "vistaprint", id: "yard-signs", name: "Yard signs + H-stakes", note: "one per install, ask at walk-through" },
  { partner: "vistaprint", id: "door-hangers", name: "Door hangers", note: "the 8 neighbors who watched the install" },
  { partner: "kodiak", id: "tumblers", name: "Engraved tumblers", note: "closing gift at the final walk-through" },
  { partner: "kodiak", id: "hats", name: "Custom hats", note: "crew + customer giveaways" },
  { partner: "kodiak", id: "apparel", name: "Crew tees / hoodies", note: "logo front & back — rolling billboards" },
  { partner: "kodiak", id: "coolers", name: "Engraved coolers", note: "referral-program grand prize" },
];

function logSwag(type: string, payload: any) {
  try { const DB: any = window.VFP_DB; DB && DB.emitEvent && DB.emitEvent(type, payload).catch(() => {}); } catch (e) {}
}

/* section = "packet" (the folder every customer gets — Settings → Job Packet
   Sheets) or "swag" (the partner order builder — FW Admin only for now: it is
   about print assets and partners, not a job, and it hasn't found its home). */
function JobPacketPage({ store, go, section = "packet" }: any) {
  const swagOnly = section === "swag";
  const pk = (store.settings && store.settings.jobPacket) || {};
  const include = Object.assign({ blueprint: true, specs: true }, pk.include || {});
  const docs = Array.isArray(pk.docs) ? pk.docs : [];
  const swag = pk.swag || {};   // itemId → qty (the saved pack draft)
  const fileRef = React.useRef<any>(null);
  const [busy, setBusy] = React.useState<any>(false);
  const save = (patch: any) => store.setSettings({ jobPacket: { ...pk, include, docs, swag, ...patch } });
  const setInclude = (k: string) => save({ include: { ...include, [k]: !include[k] } });
  const setDocs = (next: any[]) => save({ docs: next });
  const setQty = (id: string, qty: any) => save({ swag: { ...swag, [id]: Math.max(0, Math.round(+qty || 0)) } });

  async function addFiles(files: FileList) {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const pages: string[] = [];
      for (const f of Array.from(files).slice(0, 10)) {
        try { pages.push(await downscaleToDataUrl(f, 1600, 0.82)); } catch (e) {}
      }
      if (pages.length) {
        const title = String(files[0].name || "Document").replace(/\.[a-z0-9]+$/i, "").replace(/[-_]+/g, " ");
        setDocs([...docs, { id: "pd-" + Date.now(), title, pages, on: true }]);
      }
    } finally { setBusy(false); }
  }
  const move = (i: number, dir: number) => {
    const next = docs.slice(); const j = i + dir;
    if (j < 0 || j >= next.length) return;
    const t = next[i]; next[i] = next[j]; next[j] = t; setDocs(next);
  };

  const picked = SWAG_ITEMS.filter(it => (swag[it.id] || 0) > 0);
  const pickedBy = (p: string) => picked.filter(it => it.partner === p);
  const co = store.company || {};
  function orderKodiak() {
    const items = pickedBy("kodiak");
    if (!items.length) return;
    logSwag("swag.order", { partner: "kodiak", items: items.map(it => ({ id: it.id, qty: swag[it.id] })) });
    const body = [
      `Swag pack order — via FenceWorks`,
      ``,
      ...items.map(it => `• ${it.name} — qty ${swag[it.id]}`),
      ``,
      `Company: ${co.name || ""}`,
      `Contact: ${co.phone || ""} · ${co.email || ""}`,
      `Ship to: ${co.address || ""}`,
      ``,
      `Logo attached / on file — please send engraving proofs before production.`,
    ].join("\n");
    window.location.href = `mailto:${SWAG_PARTNERS.kodiak.orderEmail}?subject=${encodeURIComponent("Swag pack order — " + (co.name || "FenceWorks contractor"))}&body=${encodeURIComponent(body)}`;
  }
  function orderVistaprint() {
    const items = pickedBy("vistaprint");
    logSwag(items.length ? "swag.order" : "swag.click", { partner: "vistaprint", items: items.map(it => ({ id: it.id, qty: swag[it.id] })) });
    window.open(SWAG_PARTNERS.vistaprint.url, "_blank", "noopener");
  }

  const BUILTINS = [
    ["blueprint", "Fence plan — blueprint", "dimensioned runs, posts, gates, QR to the portal"],
    ["specs", "Install specs & pre-job", "post depth/frost line, checklist, payment terms, QRs"],
  ];
  return (
    <div className="page fade-up" style={{ maxWidth: 1100 }}>
      <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        {swagOnly ? <div>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Swag &amp; Print Partners</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Partner order builder (Vistaprint, Kodiak Wholesale). Owner-only while it finds its home — it belongs with print assets, not with a job.</p>
        </div> : <div>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Job Packet</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>The folder every customer gets — generated sheets + your own documents, printed from any quote's <b style={{ color: "var(--fg-2)" }}>Fence plan</b> button. Every page carries a QR back to their portal.</p>
        </div>}
        {!swagOnly && <button className="btn btn-ghost" onClick={() => go("estimates")}><Icon name="send" size={15} />Open an estimate to print one</button>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 18, alignItems: "start", maxWidth: 640 }}>
        {/* ---- packet composition ---- */}
        {!swagOnly && <div className="card pad">
          <div className="row gap-8 kicker kicker-teal" style={{ marginBottom: 10 }}><Icon name="book" size={14} />Packet contents</div>
          <div className="kicker" style={{ marginBottom: 8 }}>Generated sheets</div>
          <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>
            {BUILTINS.map(([k, lb, sub]: any) => (
              <button key={k} className={"toggle-row" + (include[k] ? " on" : "")} style={{ width: "100%" }} onClick={() => setInclude(k)}>
                <span className={"sw" + (include[k] ? " on" : "")}><span className="knob" /></span>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{lb}</span>
                <span className="muted" style={{ fontSize: 11.5, marginLeft: "auto", textAlign: "right" }}>{sub}</span>
              </button>
            ))}
          </div>
          <p className="faint" style={{ fontSize: 11, margin: "0 0 14px" }}>The Quote &amp; Invoice prints from the quote page itself (it carries its own e-sign QR).</p>

          <div className="row between" style={{ marginBottom: 8 }}>
            <span className="kicker">Your documents</span>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple style={{ display: "none" }}
              onChange={e => { const fs = e.target.files; if (fs) addFiles(fs); e.currentTarget.value = ""; }} />
            <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>
              <Icon name="plus" size={13} />{busy ? "Adding…" : "Add document"}
            </button>
          </div>
          {docs.length === 0
            ? <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
                Upload your existing sheets as images (PNG/JPG — one image per page): "Why driven steel posts", warranty card, referral offer, review card, care guide.
                Generic FenceWorks templates you can customize are coming; your current company versions drop straight in today.
              </p>
            : <div style={{ display: "grid", gap: 8 }}>
                {docs.map((d: any, i: number) => (
                  <div key={d.id} className="row gap-8" style={{ padding: "8px 12px", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <button className={"sw" + (d.on !== false ? " on" : "")} title={d.on !== false ? "Included in the packet" : "Excluded"} style={{ flexShrink: 0 }}
                      onClick={() => setDocs(docs.map((x: any) => x.id === d.id ? { ...x, on: x.on === false } : x))}><span className="knob" /></button>
                    <input className="input" style={{ flex: "1 1 160px", fontSize: 13 }} value={d.title || ""}
                      onChange={e => setDocs(docs.map((x: any) => x.id === d.id ? { ...x, title: e.target.value } : x))} />
                    <span className="muted" style={{ fontSize: 11.5, flexShrink: 0 }}>{(d.pages || []).length} pg</span>
                    <span className="row" style={{ gap: 2, flexShrink: 0 }}>
                      <button className="btn btn-ghost btn-sm" style={{ padding: "2px 7px" }} title="Move up" onClick={() => move(i, -1)}>↑</button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: "2px 7px" }} title="Move down" onClick={() => move(i, 1)}>↓</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)", padding: "2px 7px" }} title="Remove"
                        onClick={async () => { if (await confirmDialog({ title: `Remove "${d.title}" from the packet?`, confirmLabel: "Remove", danger: true })) setDocs(docs.filter((x: any) => x.id !== d.id)); }}><Icon name="trash" size={13} /></button>
                    </span>
                  </div>
                ))}
                <p className="faint" style={{ fontSize: 11, margin: 0 }}>Pages print full-size in this order, after the generated sheets. Keep it to a few pages — they save with your workspace.</p>
              </div>}
        </div>}

        {/* ---- swag pack builder (FW Admin) ---- */}
        {swagOnly && <div className="card pad">
          <div className="row gap-8 kicker kicker-teal" style={{ marginBottom: 6 }}><Icon name="box" size={14} />Build your swag pack</div>
          <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>Pick what goes in your pack — we send the order to the partner. Folder + engraved tumbler at the final walk-through pairs with the review QR already on your specs sheet.</p>
          {["vistaprint", "kodiak"].map(pid => {
            const P = SWAG_PARTNERS[pid];
            return (
              <div key={pid} style={{ marginBottom: 14 }}>
                <div className="row gap-8" style={{ alignItems: "center", marginBottom: 8 }}>
                  <b style={{ fontSize: 13.5 }}>{P.name}</b>
                  <span className="chip" style={{ fontSize: 9.5 }}>{P.tag}</span>
                  <span className="muted" style={{ fontSize: 11.5, marginLeft: "auto" }}>{P.blurb}</span>
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  {SWAG_ITEMS.filter(it => it.partner === pid).map(it => (
                    <div key={it.id} className="row gap-8" style={{ padding: "7px 10px", background: "var(--surface-2)", border: "1px solid " + ((swag[it.id] || 0) > 0 ? "var(--teal)" : "var(--line)"), borderRadius: 10, alignItems: "center" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{it.name}</div>
                        <div className="faint" style={{ fontSize: 11 }}>{it.note}</div>
                      </div>
                      <input className="input tnum" type="number" min="0" step="1" placeholder="0" style={{ width: 74, flexShrink: 0 }}
                        value={swag[it.id] || ""} onChange={e => setQty(it.id, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="row gap-8" style={{ flexWrap: "wrap", borderTop: "1px solid var(--line)", paddingTop: 12 }}>
            <button className="btn btn-primary btn-sm" disabled={pickedBy("kodiak").length === 0} onClick={orderKodiak}
              title="Opens a prefilled order email to Kodiak Wholesale with your pack + company info">
              <Icon name="send" size={13} />Send order — Kodiak Wholesale
            </button>
            <button className="btn btn-outline btn-sm" onClick={orderVistaprint}
              title="Opens Vistaprint to place the print order (your pack picks ride along in our records)">
              Order at Vistaprint<Icon name="chevright" size={13} />
            </button>
            {picked.length > 0 && <span className="muted" style={{ fontSize: 12, alignSelf: "center" }}>{picked.length} item{picked.length > 1 ? "s" : ""} in the pack — saved</span>}
          </div>
          <p className="faint" style={{ fontSize: 11, margin: "10px 0 0" }}>Kodiak orders send with your company + ship-to prefilled; engraving proofs come back by email before production. Direct Vistaprint API ordering is on the roadmap — today the pack opens their configured storefront.</p>
        </div>}
      </div>
    </div>
  );
}

Object.assign(window, { JobPacketPage });

export { JobPacketPage, SWAG_PARTNERS, SWAG_ITEMS };
